/** @jsxImportSource @emotion/react */
import * as React from "react";
import styled from "@emotion/styled";
import { EntityBadge, H3, IconCalendar, Link } from "atoms";
import { Thumbnail } from "features/images";
import { featureFlags } from "config";
import { formatDate } from "utils/date";
import featuredProjectIcon from "atoms/icons/featured-project.svg";

import type { Artist, Project } from "../types";
import { HugScore } from "./HugScore";

interface Props {
  entity: Project | Artist;
  isFeatured?: boolean;
  isMintingSoon?: boolean;
}

function ReviewableCard({
  entity,
  isFeatured: isFeaturedProp = false,
  isMintingSoon = false,
  ...props
}: Props) {
  const { id, name, type, hugScore, heroImage, reviewCount, projectLists } =
    entity;

  const [isSquarish, setIsSquarish] = React.useState(true);
  const handleImgLoad: React.ReactEventHandler<HTMLImageElement> = (event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    setIsSquarish(Math.abs(naturalWidth / naturalHeight - 1) <= 0.1);
  };

  const isProject = type === "PROJECT";
  const isArtist = type === "ARTIST";

  const isFeatured =
    projectLists.includes("project-of-the-month") || isFeaturedProp;
  const entityURL = `/${type.toLowerCase()}s/${id}`;

  return (
    <Card {...props} className="ProjectCard">
      <CardImageWrapper isFeatured={isFeatured}>
        {(!isSquarish || isArtist) && (
          <CardImageBackground src={heroImage} alt="" aria-hidden="true" />
        )}
        <CardImage
          onLoad={handleImgLoad}
          src={heroImage}
          alt={`Featured NFT from ${name} collection`}
          isSquarish={isSquarish}
          circle={isArtist}
          rounded={isProject}
        />
      </CardImageWrapper>
      <CardHeader>
        <CardTitle>
          <SH3 size="xs">
            <SLink to={entityURL}>{name}</SLink>
          </SH3>
        </CardTitle>
        {featureFlags.enableProjectInteractions && !isMintingSoon && isProject && (
          <Interactions>
            <HugScore score={hugScore} reviewCount={reviewCount} />
            <Reviews>
              {!reviewCount ? (
                <small>Be the first to&nbsp;review</small>
              ) : (
                `${reviewCount} Review${reviewCount > 1 ? "s" : ""}`
              )}
            </Reviews>
            <ReviewableTypeLabel type={entity.type} />
          </Interactions>
        )}
        {isArtist && (
          <Interactions>
            <Reviews>
              {!reviewCount ? (
                <small>Be the first to review</small>
              ) : (
                `${reviewCount} Review${reviewCount > 1 ? "s" : ""}`
              )}
            </Reviews>
            <ReviewableTypeLabel type={entity.type} />
          </Interactions>
        )}
      </CardHeader>

      {featureFlags.enableProjectInteractions && isMintingSoon && isProject && (
        <Interactions>
          <MintDate>
            <div>
              <small>Minting on</small>
              {formatDate(
                entity.mintDate,
                {
                  month: "long",
                  day: "2-digit",
                },
                "TBD"
              )}
            </div>
            <IconCalendar />
          </MintDate>
        </Interactions>
      )}
    </Card>
  );
}

const Card = styled.article(({ theme }) => ({
  background: theme.colors.bgLight,
  borderRadius: theme.borderRadius.md,
  boxShadow: theme.boxShadow.light,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  position: "relative",
}));

const CardHeader = styled.div({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: ".5em",
  justifyContent: "space-between",
  padding: ".5em",
  "@media (min-width: 768px)": {
    gap: ".75em",
    padding: ".75rem",
  },
  "@supports (gap: clamp(.5rem, 1vw, 1rem))": {
    gap: "clamp(.5rem, 1vw, 1rem)",
    padding: "clamp(.5rem, 1vw, 1rem)",
  },
});
const CardTitle = styled.header(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flex: 1,
  fontSize: theme.fontSizes.xs,
  paddingTop: ".1em",
  button: {
    position: "absolute",
    top: "0.5rem",
    right: "0.5rem",
  },
  "&:only-child": {
    alignItems: "flex-start",
  },
}));
const SH3 = styled(H3)({
  alignItems: "center",
  display: "flex",
  fontWeight: "bold",
  lineHeight: "1.1",
  margin: 0,
});

const SLink = styled(Link)({
  display: "inline-block",
  textDecoration: "none",
  "&::after": {
    content: "''",
    bottom: 0,
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 3,
  },
});

const CardImageWrapper = styled("figure", {
  shouldForwardProp: (prop) => prop !== "isFeatured",
})<{ isFeatured?: boolean }>(({ isFeatured = false, theme }) => ({
  alignItems: "center",
  aspectRatio: "1",
  display: "flex",
  flex: "0 0 auto",
  justifyContent: "center",
  margin: 0,
  overflow: "hidden",
  position: "relative",
  transition: "opacity 0.1s ease",
  "&:hover": {
    opacity: 0.7,
  },
  ...(isFeatured
    ? {
        "&::after": {
          content: "''",
          backgroundImage: `url(${featuredProjectIcon})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "top right",
          backgroundSize: "contain",
          display: "block",
          height: "72px",
          pointerEvents: "none",
          position: "absolute",
          right: "10px",
          top: "10px",
          width: "130px",
          maxWidth: "33%",
          minWidth: "71px",
          zIndex: 3,
        },
      }
    : {}),
}));

const CardImage = styled(Thumbnail)<{ isSquarish: boolean; circle: boolean }>(
  ({ isSquarish, circle, theme }) => ({
    height: "100%",
    objectPosition: "50% 50%",
    position: "relative",
    zIndex: "2",
    width: "100%",
    objectFit: circle || isSquarish ? "cover" : "contain",
    ...(circle
      ? {
          transform: "scale(.9)",
        }
      : {
          borderRadius: 0,
        }),
  })
);

const CardImageBackground = styled(Thumbnail)({
  bottom: "-10px",
  left: "-10px",
  opacity: ".33",
  position: "absolute",
  top: "-10px",
  width: "calc(100% + 20px)",
  height: "calc(100% + 20px)",
  right: "-10px",
  zIndex: "1",
  filter: "blur(10px)",
  objectFit: "cover",
});

const Interactions = styled.div({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  position: "relative",
  zIndex: 1,
});
const Reviews = styled.div(({ theme }) => ({
  fontSize: theme.fontSizes.xxxs,
  fontWeight: "bold",
  lineHeight: 1.3,
  opacity: ".75",
  textAlign: "center",
  small: {
    fontSize: "85%",
  },
}));

const MintDate = styled.div(({ theme }) => ({
  alignItems: "center",
  borderRadius: theme.borderRadius.sm,
  display: "flex",
  flex: 1,
  fontSize: theme.fontSizes.xs,
  fontWeight: "bold",
  justifyContent: "space-between",
  position: "relative",
  margin: "0 .5em .5em .5em",
  padding: ".5em",
  "@media (min-width: 768px)": {
    padding: ".75rem",
  },
  "@supports (padding: clamp(.5rem, 1vw, 1rem))": {
    padding: "clamp(.5rem, 1vw, 1rem)",
  },
  "&::before": {
    backgroundColor: theme.colors.yellow,
    borderRadius: theme.borderRadius.sm,
    bottom: 0,
    content: "''",
    left: 0,
    opacity: ".3",
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: "-1",
  },
  small: {
    ...theme.fonts.body,
    display: "block",
    fontSize: theme.fontSizes.xxxs,
    letterSpacing: ".03em",
    textTransform: "uppercase",
  },
}));

interface ReviewableTypeLabelProps {
  type: string;
}
function ReviewableTypeLabel({ type }: ReviewableTypeLabelProps) {
  return (
    <EntityBadge>
      <span>
        {type[0]}
        {type.slice(1).toLowerCase()}
      </span>
    </EntityBadge>
  );
}

export { ReviewableCard, CardImage, CardImageWrapper };
